/**
 * Stale-build watcher. A deploy renames every hashed chunk; a running SPA session
 * still holds the old index.html in memory, so its next lazy route import 404s and
 * the vite:preloadError handler (main.tsx) hard-reloads mid-action — the "white
 * flash". This watcher notices the new build BEFORE any import breaks: it compares
 * the entry chunk this document booted from against the entry chunk in a freshly
 * fetched /index.html. Once they differ, the router (see router.tsx) turns the NEXT
 * user navigation into a full page load — felt as an ordinary link click, and never
 * fired while the user is standing still (typing a form, playing a quiz).
 *
 * Positive-match, fail-closed: "stale" only when the fetched HTML parses to an entry
 * src that DIFFERS. A 502 during the deploy window, an error page, a captive portal —
 * none of those parse, so none of them can steer a still-working SPA into a full
 * navigation onto a broken page. (Not a service-worker concern: sw.js is push-only
 * and caches nothing; the stale copy lives in this document's own module graph.)
 */

// Matches the single Vite entry tag: <script type="module" crossorigin src="/assets/index-<hash>.js">
// modulepreload hints are <link> elements and dev serves /src/main.tsx — neither matches.
const ENTRY_SCRIPT_RE = /<script[^>]*\btype="module"[^>]*\bsrc="([^"]*\/assets\/[^"]+)"/

export function extractEntryScriptSrc(html: string): string | null {
  return ENTRY_SCRIPT_RE.exec(html)?.[1] ?? null
}

const CHECK_INTERVAL_MS = 5 * 60_000
// Visibility can flap rapidly (alt-tabbing); one fetch a minute is plenty for an
// internal tool where deploys are hours apart.
const MIN_CHECK_GAP_MS = 60_000

let stale = false
let entrySrc: string | null = null
let lastCheckAt = 0

export function buildIsStale(): boolean {
  return stale
}

async function check(): Promise<void> {
  if (stale || !entrySrc) return
  const now = Date.now()
  if (now - lastCheckAt < MIN_CHECK_GAP_MS) return
  lastCheckAt = now
  try {
    const res = await fetch('/index.html', { cache: 'no-store' })
    if (!res.ok) return
    const liveSrc = extractEntryScriptSrc(await res.text())
    if (liveSrc && liveSrc !== entrySrc) stale = true
  } catch {
    // Offline or mid-deploy hiccup — the next trigger retries.
  }
}

export function startStaleBuildWatcher(): void {
  entrySrc =
    document
      .querySelector('script[type="module"][src*="/assets/"]')
      ?.getAttribute('src') ?? null
  // Dev server (src="/src/main.tsx") or an unexpected document shape: stay inert
  // rather than guess — the preloadError backstop still covers real breakage.
  if (!entrySrc) return

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check()
  })
  // bfcache / iOS PWA resume restores the page without a visibilitychange.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) void check()
  })
  setInterval(() => {
    if (document.visibilityState === 'visible') void check()
  }, CHECK_INTERVAL_MS)
}
